import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * ParallaxSection
 * ───────────────
 * Wraps a section so its content drifts at a slightly different speed
 * than the page scroll, creating a depth/floating effect.
 * Powered by GSAP ScrollTrigger + Lenis for silky smoothness.
 *
 * Props:
 *   speed     {number}  Parallax intensity. 0 = none, 0.9 = strong drift.
 *   className {string}  Extra classes on the outer wrapper.
 */
function ParallaxSection({ children, speed = 0.9, className = '' }) {
    const outerRef = useRef(null);
    const innerRef = useRef(null);

    useEffect(() => {
        const outer = outerRef.current;
        const inner = innerRef.current;
        if (!outer || !inner) return;

        const distance = speed * 80; // px travel total across the scroll range

        const tween = gsap.fromTo(
            inner,
            { y: -distance },
            {
                y: distance,
                ease: 'none',
                scrollTrigger: {
                    trigger: outer,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: 1.5,
                    invalidateOnRefresh: true,
                },
            }
        );

        return () => {
            tween.scrollTrigger?.kill();
            tween.kill();
        };
    }, [speed]);

    return (
        <div ref={outerRef} className={`${className}`}
        style={{overflow : "hidden"}}
        >
            <div ref={innerRef} style={{ willChange: 'transform' }}>
                {children}
            </div>
        </div>
    );
}

export default ParallaxSection;
